export interface ProductImagesValue {
  mainImage: string;
  galleryImageUrls: string[];
}

export function parseImagesJson(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is string => typeof item === 'string' && item.length > 0);
    }
  } catch {
    return raw
      .split(/\s*,\s*/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

/** Build ordered URL list: cover first, then gallery without duplicating cover. */
export function mergeProductImages(mainImage: string, galleryImageUrls: string[]): string[] {
  const main = mainImage.trim();
  const gallery = galleryImageUrls.map((url) => url.trim()).filter(Boolean);
  if (!main) return gallery;
  return [main, ...gallery.filter((url) => url !== main)];
}

/** Split ordered URL list into cover + gallery. */
export function splitProductImages(urls: string[]): ProductImagesValue {
  const cleaned = urls.map((url) => url.trim()).filter(Boolean);
  if (cleaned.length === 0) {
    return { mainImage: '', galleryImageUrls: [] };
  }
  return {
    mainImage: cleaned[0]!,
    galleryImageUrls: cleaned.slice(1),
  };
}

export function snapshotToProductImages(snapshot: {
  mainImage: string | null;
  images: string | null;
}): ProductImagesValue {
  const gallery = parseImagesJson(snapshot.images);
  const main = (snapshot.mainImage ?? '').trim();
  if (!main) {
    return splitProductImages(gallery);
  }
  return splitProductImages([main, ...gallery.filter((url) => url !== main)]);
}

export function moveProductImage(urls: string[], fromIndex: number, toIndex: number): string[] {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= urls.length ||
    toIndex >= urls.length
  ) {
    return urls;
  }
  const next = [...urls];
  const [item] = next.splice(fromIndex, 1);
  if (item == null) return urls;
  next.splice(toIndex, 0, item);
  return next;
}
